import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useQuery } from '@apollo/client';
import { getUser } from 'helpers/token';
import { GET_USER_COUNT } from '../../../helpers/graphql/userCount';
import { figtree } from '../../Landing/fonts';
import Footer from '../../General/Footer';
import Header from '../../Header/Header';
import LandingNav from '../../Landing/LandingNav';
import AiHero from './AiHero';
import AiFlightPlan from './AiFlightPlan';
import AiKit from './AiKit';
import AiFinalCta from './AiFinalCta';

// Auth dialogs stay out of the bundle until a CTA is clicked — same
// pattern as the landing page.
const JoinDialog = dynamic(() => import('../../Header/JoinDialog'), {
  ssr: false,
});
const LoginDialog = dynamic(() => import('../../Header/LoginDialog'), {
  ssr: false,
});
const PasswordResetDialog = dynamic(
  () => import('../../Header/PasswordResetDialog'),
  { ssr: false },
);

const NAV_LINKS = [
  { label: 'How it works', href: '#how' },
  { label: 'What it refuses to do', href: '#kit' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Explore stories', href: '/explore' },
];

// /ai marketing page in the expedition-journal design language. Members
// keep the classic Header; logged-out visitors get the landing nav with
// the join/login dialogs.
export default function AiLandingPage() {
  const [user, setUser] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [dialogsReady, setDialogsReady] = useState(false);

  const { data } = useQuery(GET_USER_COUNT);
  const userCount = data?.userCount || 4000;

  useEffect(() => {
    setUser(getUser());
  }, []);

  // Paper-colored document bg while mounted, like the landing page.
  useEffect(() => {
    document.body.classList.add('tf-landing');
    return () => document.body.classList.remove('tf-landing');
  }, []);

  const openJoin = () => {
    setDialogsReady(true);
    setJoinOpen(true);
  };
  const openLogin = () => {
    setDialogsReady(true);
    setLoginOpen(true);
  };
  const switchDialog = () => {
    setJoinOpen(prev => !prev);
    setLoginOpen(prev => !prev);
  };
  const loginResetSwitch = () => {
    setResetOpen(prev => !prev);
    setLoginOpen(prev => !prev);
  };
  const handleJoinClose = (_, reason) => {
    if (reason !== 'backdropClick') setJoinOpen(false);
  };

  return (
    <div
      className={`${figtree.className} tw-min-h-screen tw-bg-[#FAF8F1] tw-text-[#1D2D28] dark:tw-bg-[#0E1A16] dark:tw-text-[#EBE6D9] tw-antialiased`}
    >
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      {user ? (
        <Header active="ai" />
      ) : (
        <LandingNav onJoin={openJoin} onLogin={openLogin} links={NAV_LINKS} />
      )}

      <main>
        <AiHero user={user} onJoin={openJoin} userCount={userCount} />
        <AiFlightPlan />
        <AiKit />
        <AiFinalCta user={user} onJoin={openJoin} />
      </main>

      <Footer />

      {dialogsReady && (
        <>
          <LoginDialog
            switch={switchDialog}
            handleLoginResetSwitch={loginResetSwitch}
            open={loginOpen}
            handleClose={() => setLoginOpen(false)}
          />
          <JoinDialog
            switch={switchDialog}
            open={joinOpen}
            handleClose={handleJoinClose}
          />
          <PasswordResetDialog
            switch={loginResetSwitch}
            open={resetOpen}
            handleClose={() => setResetOpen(false)}
          />
        </>
      )}
    </div>
  );
}
